import { baseUrl } from '../config/constants';

export type KycStatus = 'pending' | 'under_review' | 'approved' | 'rejected';

export interface StoreInfo {
    id: string;
    name: string;
    slug: string;
    subdomain: string;
    kycStatus?: KycStatus;
}

// Header Overlay Configuration
export interface HeaderOverlayConfig {
    enabled: boolean;
    text: string;
    font: string | null; // Google Font name or null for local
    fontSize: number; // in rem
    fontWeight: number; // 400, 500, 600, 700, 800, 900
    color: string; // hex color
    position: {
        x: number; // 0-100 percentage
        y: number; // 0-100 percentage
    };
    textAlign: 'left' | 'center' | 'right';
    textShadow: {
        enabled: boolean;
        color: string;
        blur: number;
        offsetX: number;
        offsetY: number;
    };
    letterSpacing: number;
    vignette: {
        enabled: boolean;
        color: string;
        intensity: number; // 0-1
    };
}

// Custom screen configuration (for store-specific screens)
export interface CustomScreen {
    name: string;
    displayName: string;
    screenKey: string;
    showInNavBar: boolean;
    showInDrawer: boolean;
    sortOrder: number;
    icon?: string | null;
    route?: string | null;
    heroVideoUrl?: string | null;
    heroImageUrl?: string | null;
    heroOverlayOpacity?: number | null;
}

// Product Detail Section configuration
export interface ProductDetailSection {
    id: string;
    title: string;
    content: string;
    isEnabled: boolean;
    sortOrder: number;
    isDefault?: boolean;
}

export interface ProductDetailSectionsConfig {
    sections: ProductDetailSection[];
}

export interface StoreConfig {
    // Branding
    companyName: string;
    appHeaderName: string;
    footerAppName: string;
    footerDescription: string;
    appLogo: string;
    currency: string;
    vatPercentage: number;
    
    // Theme
    primaryColor: string;
    themeMode: 'light' | 'dark' | 'dynamic';
    
    // Typography (null = use local fonts)
    primaryFont?: string | null;
    secondaryFont?: string | null;
    logoFont?: string | null;
    
    // Customization - Header Media (image or video)
    appHeaderImage?: string | null;
    appHeaderMediaType?: 'image' | 'video';
    appHeaderPosition?: { x: number; y: number };
    appHeaderScale?: number;
    
    // Header Overlay
    headerOverlay?: HeaderOverlayConfig | null;
    
    // Splash Screen
    splashLogo?: string | null;
    splashTintColor?: string | null;
    splashLogoTintColor?: string | null;
    
    // Sharing Images for Open Graph / Social Media
    sharingImageSquare?: string | null;
    sharingImageLandscape?: string | null;
    
    // Product Settings
    maxProductImages: number;
    maxImageSizeMB: number;
    allowedImageTypes: string[];
    
    // Stock Thresholds
    stockLowThreshold: number;
    stockMediumThreshold: number;
    
    // Social Media Links
    socialFacebook: string;
    socialInstagram: string;
    socialTwitter: string;
    socialLinkedin: string;
    socialTiktok: string;
    
    // Product Detail Sections (from JSONB config)
    productDetailSections?: ProductDetailSectionsConfig;
    
    // Extended config (from JSONB)
    [key: string]: any;
}

export interface StoreConfigResponse {
    store: StoreInfo;
    config: StoreConfig;
    screens?: CustomScreen[];
}

export class StoreInactiveError extends Error {
    public readonly storeName: string;
    public readonly primaryColor?: string;
    
    constructor(storeName: string, primaryColor?: string) {
        super('Store is currently under maintenance');
        this.name = 'StoreInactiveError';
        this.storeName = storeName;
        this.primaryColor = primaryColor;
    }
}

export class StoreNotFoundError extends Error {
    public readonly subdomain: string;
    
    constructor(subdomain: string) {
        super('Store not found');
        this.name = 'StoreNotFoundError';
        this.subdomain = subdomain;
    }
}

class ConfigService {
    private config: StoreConfigResponse | null = null;
    private loading: boolean = false;
    private loadPromise: Promise<StoreConfigResponse> | null = null;

    /**
     * Get the current subdomain from the hostname
     */
    private getSubdomain(): string {
        const hostname = window.location.hostname;
        
        // Local development with subdomain
        if (hostname.endsWith('.localhost')) {
            const subdomain = hostname.split('.')[0];
            return subdomain;
        }
        
        // Local development (plain localhost or 127.0.0.1)
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            try {
                // Read from synced config file (created by yarn store:sync)
                const syncedConfig = require('../../../.store-config.json');
                
                if (syncedConfig && syncedConfig.store && syncedConfig.store.subdomain) {
                    console.log(`📁 [ConfigService] Using synced store: ${syncedConfig.store.subdomain}`);
                    return syncedConfig.store.subdomain;
                }
            } catch (error) {
                // .store-config.json doesn't exist or is invalid
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                console.error('Failed to load .store-config.json:', errorMessage);
            }
            
            // No synced config found - redirect to shop
            if (typeof window !== 'undefined') {
                window.location.replace('https://cosmocart.shop');
            }
            
            return '';
        }

        // Cloudflare Pages: Handle *.pages.dev domains
        // Examples: demo.cosmo-template.pages.dev, rocket.cosmo-template.pages.dev
        if (hostname.endsWith('.pages.dev')) {
            const parts = hostname.split('.');
            
            // If it's subdomain.cosmo-template.pages.dev (4 parts), extract the subdomain
            if (parts.length === 4) {
                const subdomain = parts[0];
                return subdomain;
            }
            
            // If it's just cosmo-template.pages.dev (3 parts), no store specified - redirect to landing
            if (parts.length === 3) {
                if (typeof window !== 'undefined') {
                    window.location.replace('https://cosmocart.shop');
                }
                
                return '';
            }
        }

        // Custom domains: cosmocart.shop, cosmocart.shop
        // Examples: "demo.cosmocart.shop" -> "demo", "rocket.cosmocart.shop" -> "rocket"
        const parts = hostname.split('.');
        if (parts.length >= 3) {
            const subdomain = parts[0];
            
            // Don't treat 'www' as a store subdomain
            if (subdomain === 'www') {
                if (typeof window !== 'undefined') {
                    window.location.replace('https://cosmocart.shop');
                }
                
                return '';
            }
            
            return subdomain;
        }

        // Production: Main domain with no subdomain - redirect to landing page
        if (typeof window !== 'undefined') {
            window.location.replace('https://cosmocart.shop');
        }
        
        // Return empty string to satisfy TypeScript (this code won't execute after redirect)
        return '';
    }

    /**
     * Load store configuration from API
     */
    async loadConfig(): Promise<StoreConfigResponse> {
        // Return cached config if already loaded
        if (this.config) {
            return this.config;
        }

        // Return existing promise if already loading
        if (this.loadPromise) {
            return this.loadPromise;
        }

        const subdomain = this.getSubdomain();

        this.loading = true;
        this.loadPromise = this.fetchConfig(subdomain);

        try {
            this.config = await this.loadPromise;
            return this.config;
        } catch (error) {
            console.error('❌ [ConfigService] Failed to load config:', error);
            throw error;
        } finally {
            this.loading = false;
            this.loadPromise = null;
        }
    }

    /**
     * Fetch configuration from API
     */
    private async fetchConfig(subdomain: string): Promise<StoreConfigResponse> {
        const response = await fetch(`${baseUrl}/config?subdomain=${subdomain}`);

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Failed to load config' }));
            console.error('[ConfigService] ❌ API error:', error);
            
            // Check if store is not found (404 or specific error message)
            const isNotFound = 
                response.status === 404 ||
                (typeof error.message === 'string' && error.message.includes('Store not found')) ||
                (typeof error === 'object' && error.message?.message?.includes('Store not found'));
            
            if (isNotFound) {
                throw new StoreNotFoundError(subdomain);
            }
            
            // Check if store is inactive
            const isInactive = 
                error.message === 'Store is inactive' || 
                (typeof error === 'object' && error.message?.message === 'Store is inactive');
            
            if (isInactive) {
                const storeName = error.storeName || error.message?.storeName || subdomain;
                const primaryColor = error.primaryColor || error.message?.primaryColor;
                throw new StoreInactiveError(storeName, primaryColor);
            }
            
            const errorMessage = typeof error === 'string' ? error : (error.message || 'Failed to load store configuration');
            throw new Error(errorMessage);
        }

        const result = await response.json();

        // API returns data directly, not wrapped
        if (!result.store || !result.config) {
            console.error('[ConfigService] ❌ Invalid response format:', result);
            throw new Error('Invalid config response format');
        }

        return result;
    }

    /**
     * Get cached configuration (must call loadConfig first)
     */
    getConfig(): StoreConfigResponse | null {
        return this.config;
    }

    /**
     * Get specific config value
     */
    get<K extends keyof StoreConfig>(key: K): StoreConfig[K] | undefined {
        return this.config?.config[key];
    }

    /**
     * Get store info
     */
    getStore(): StoreInfo | null {
        return this.config?.store || null;
    }

    /**
     * Check if config is loaded
     */
    isLoaded(): boolean {
        return this.config !== null;
    }

    /**
     * Check if currently loading
     */
    isLoading(): boolean {
        return this.loading;
    }

    /**
     * Clear cached config (force reload)
     */
    clearConfig(): void {
        this.config = null;
        this.loading = false;
        this.loadPromise = null;
    }

    /**
     * Reload configuration
     */
    async reloadConfig(): Promise<StoreConfigResponse> {
        this.clearConfig();
        return this.loadConfig();
    }
}

// Export singleton instance
export const configService = new ConfigService();

// Export helper hook for React components
export function useStoreConfig() {
    // This can be enhanced to use React Context or state management
    const config = configService.getConfig();
    return {
        config,
        isLoaded: configService.isLoaded(),
        isLoading: configService.isLoading(),
        loadConfig: () => configService.loadConfig(),
        reloadConfig: () => configService.reloadConfig()
    };
}

